<template>
  <div class="image-inpainting-editor">
    <!-- Ретушь (inpainting/*) — только админу: для остальных бэкенд отдаёт 403, остаётся просмотр -->
    <div v-if="$can('manage', 'all')" class="d-flex justify-content-between align-items-center mb-1">
      <div class="d-flex align-items-center" style="gap: 0.5rem;">
        <b-button
            :variant="isDrawing ? 'danger' : 'outline-primary'"
            size="sm"
            @click="toggleDrawing"
        >
          <feather-icon :icon="isDrawing ? 'XIcon' : 'Edit2Icon'" class="mr-25" />
          {{ isDrawing ? 'Отключить кисть' : 'Редактировать' }}
        </b-button>
        <div v-if="isDrawing" class="d-flex align-items-center" style="gap: 0.5rem;">
          <label class="mb-0 font-small-3">Размер кисти:</label>
          <input
              type="range"
              :min="5"
              :max="80"
              v-model.number="brushSize"
              style="width: 120px;"
          />
          <span class="font-small-3 text-muted">{{ brushSize }}px</span>
          <b-button
              variant="outline-warning"
              size="sm"
              @click="clearMask"
              :disabled="!hasMaskStrokes"
          >
            <feather-icon icon="Trash2Icon" class="mr-25" />
            Очистить
          </b-button>
        </div>
      </div>
      <div v-if="isProcessing" class="d-flex align-items-center text-info">
        <b-spinner small class="mr-50" />
        <span class="font-small-3">Обработка...</span>
      </div>
    </div>

    <div class="d-flex justify-content-center">
      <div
          ref="canvasContainer"
          class="position-relative d-inline-block"
          style="line-height: 0;"
      >
        <img
            ref="baseImage"
            :src="currentImageUrl"
            class="rounded"
            :class="{ 'image-processing': isProcessing }"
            :style="{ maxHeight: maxHeight + 'px', maxWidth: '100%' }"
            @load="onImageLoad"
        />
        <canvas
            v-show="isDrawing"
            ref="maskCanvas"
            class="position-absolute rounded"
            style="top: 0; left: 0; cursor: crosshair; opacity: 0.65;"
            @mousedown="startStroke"
            @mousemove="continueStroke"
            @mouseup="endStroke"
            @mouseleave="endStroke"
        />
      </div>
    </div>

    <div v-if="error" class="text-danger mt-50 font-small-3">{{ error }}</div>
  </div>
</template>

<script>
import { ref, onUnmounted, computed, nextTick, watch } from '@vue/composition-api'
import { BButton, BSpinner } from 'bootstrap-vue'
import axios from 'axios'
import axiosMain from '@axios'

// Ретушь картинки: кистью рисуется маска, через секунду после штриха область стирается Replicate'ом.
// Результат копится последовательно (следующий штрих правит уже обработанную картинку).
// Наружу: событие `result` (URL результата или null после сброса) и `processing` (идёт ли запрос);
// сброс к оригиналу — через ref: this.$refs.editor.resetToOriginal().
// Запросы — напрямую через @axios, без store: компонент живёт и вне страницы артиста (модуль artists там не зарегистрирован).
export default {
  name: 'ImageInpaintingEditor',
  components: {
    BButton,
    BSpinner,
  },
  props: {
    imageUrl: {
      type: String,
      required: true,
    },
    maxHeight: {
      type: Number,
      default: 600,
    },
  },
  setup(props, { emit }) {
    const baseImage = ref(null)
    const maskCanvas = ref(null)
    const canvasContainer = ref(null)

    const isDrawing = ref(false)
    const isStroking = ref(false)
    const brushSize = ref(30)
    const hasMaskStrokes = ref(false)
    const isProcessing = ref(false)
    const error = ref(null)
    const resultImageUrl = ref(null)

    let debounceTimer = null
    let cancelSource = null
    let maskCtx = null

    const currentImageUrl = computed(() => resultImageUrl.value || props.imageUrl)

    const DEBOUNCE_MS = 1000

    watch(resultImageUrl, value => emit('result', value))
    watch(isProcessing, value => emit('processing', value))

    function onImageLoad() {
      if (isDrawing.value) {
        setupCanvas()
      }
    }

    function setupCanvas() {
      const img = baseImage.value
      const canvas = maskCanvas.value
      if (!img || !canvas) return

      canvas.width = img.clientWidth
      canvas.height = img.clientHeight
      canvas.style.width = img.clientWidth + 'px'
      canvas.style.height = img.clientHeight + 'px'

      maskCtx = canvas.getContext('2d')
      maskCtx.lineCap = 'round'
      maskCtx.lineJoin = 'round'
    }

    function toggleDrawing() {
      isDrawing.value = !isDrawing.value
      if (isDrawing.value) {
        nextTick(() => setupCanvas())
      } else {
        clearDebounce()
        hasMaskStrokes.value = false
      }
    }

    function clearMask() {
      if (!maskCtx || !maskCanvas.value) return
      maskCtx.clearRect(0, 0, maskCanvas.value.width, maskCanvas.value.height)
      hasMaskStrokes.value = false
      clearDebounce()
      cancelPendingRequest()
    }

    function getPos(e) {
      const rect = maskCanvas.value.getBoundingClientRect()
      return {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      }
    }

    function startStroke(e) {
      isStroking.value = true
      clearDebounce()
      cancelPendingRequest()

      const pos = getPos(e)
      maskCtx.strokeStyle = '#39ff14'
      maskCtx.lineWidth = brushSize.value
      maskCtx.beginPath()
      maskCtx.moveTo(pos.x, pos.y)
      // Рисуем точку при клике
      maskCtx.lineTo(pos.x + 0.1, pos.y + 0.1)
      maskCtx.stroke()
      hasMaskStrokes.value = true
    }

    function continueStroke(e) {
      if (!isStroking.value) return
      const pos = getPos(e)
      maskCtx.lineTo(pos.x, pos.y)
      maskCtx.stroke()
    }

    function endStroke() {
      if (!isStroking.value) return
      isStroking.value = false
      maskCtx.closePath()
      startDebounce()
    }

    function clearDebounce() {
      if (debounceTimer) {
        clearTimeout(debounceTimer)
        debounceTimer = null
      }
    }

    function cancelPendingRequest() {
      if (cancelSource) {
        cancelSource.cancel('Рисование продолжено')
        cancelSource = null
      }
    }

    function startDebounce() {
      clearDebounce()
      if (!hasMaskStrokes.value) return
      debounceTimer = setTimeout(() => {
        sendInpaintingRequest()
      }, DEBOUNCE_MS)
    }

    function exportMaskAsBlob() {
      return new Promise((resolve) => {
        const img = baseImage.value
        const canvas = maskCanvas.value
        if (!img || !canvas) {
          resolve(null)
          return
        }

        // Создаём канвас размером с оригинальное изображение
        const exportCanvas = document.createElement('canvas')
        exportCanvas.width = img.naturalWidth
        exportCanvas.height = img.naturalHeight
        const ctx = exportCanvas.getContext('2d')

        const scaleX = img.naturalWidth / canvas.width
        const scaleY = img.naturalHeight / canvas.height

        // Конвертируем салатовые штрихи в белые на отдельном канвасе
        const tempCanvas = document.createElement('canvas')
        tempCanvas.width = exportCanvas.width
        tempCanvas.height = exportCanvas.height
        const tempCtx = tempCanvas.getContext('2d')
        tempCtx.scale(scaleX, scaleY)
        tempCtx.drawImage(canvas, 0, 0)
        // Заменяем цвет на белый только там, где есть штрихи
        tempCtx.setTransform(1, 0, 0, 1, 0, 0)
        tempCtx.globalCompositeOperation = 'source-in'
        tempCtx.fillStyle = '#ffffff'
        tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height)

        // Заполняем чёрным (сохраняемая область) и накладываем белые штрихи
        ctx.fillStyle = '#000000'
        ctx.fillRect(0, 0, exportCanvas.width, exportCanvas.height)
        ctx.drawImage(tempCanvas, 0, 0)

        exportCanvas.toBlob((blob) => {
          resolve(blob)
        }, 'image/png')
      })
    }

    async function sendInpaintingRequest() {
      if (isProcessing.value) {
        cancelPendingRequest()
      }

      error.value = null
      isProcessing.value = true

      try {
        // 1. Экспортируем маску
        const maskBlob = await exportMaskAsBlob()
        if (!maskBlob) {
          error.value = 'Не удалось создать маску'
          isProcessing.value = false
          return
        }

        // 2. Загружаем маску на сервер
        const maskFile = new File([maskBlob], 'mask.png', { type: 'image/png' })
        const formData = new FormData()
        formData.append('file', maskFile)
        formData.append('type', 'inpainting-mask')
        const uploadResponse = await axiosMain.post('/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        })
        const maskUrl = uploadResponse.data.url

        // 3. Отправляем запрос на удаление объекта
        cancelSource = axios.CancelToken.source()
        const response = await axiosMain.post('/inpainting/remove-object', {
          image_url: currentImageUrl.value,
          mask_url: maskUrl,
        }, { cancelToken: cancelSource.token })

        if (response.data.success && response.data.result_url) {
          resultImageUrl.value = response.data.result_url
          // Очищаем маску после успешной обработки
          if (maskCtx && maskCanvas.value) {
            maskCtx.clearRect(0, 0, maskCanvas.value.width, maskCanvas.value.height)
            hasMaskStrokes.value = false
          }
        } else {
          const msg = response.data.error || 'Не удалось обработать изображение'
          error.value = response.data.detail ? `${msg}: ${response.data.detail}` : msg
        }
      } catch (err) {
        if (axios.isCancel(err)) {
          // Запрос отменён пользователем — не показываем ошибку
          return
        }
        const data = err.response?.data
        const msg = data?.error || data?.message || 'Ошибка при обработке'
        error.value = data?.detail ? `${msg}: ${data.detail}` : msg
      } finally {
        cancelSource = null
        isProcessing.value = false
      }
    }

    function resetToOriginal() {
      resultImageUrl.value = null
      clearMask()
    }

    function resetState() {
      resultImageUrl.value = null
      hasMaskStrokes.value = false
      isDrawing.value = false
      isProcessing.value = false
      error.value = null
      clearDebounce()
      cancelPendingRequest()
    }

    watch(() => props.imageUrl, resetState)

    onUnmounted(() => {
      clearDebounce()
      cancelPendingRequest()
    })

    return {
      baseImage,
      maskCanvas,
      canvasContainer,
      isDrawing,
      brushSize,
      hasMaskStrokes,
      isProcessing,
      error,
      currentImageUrl,
      onImageLoad,
      toggleDrawing,
      clearMask,
      startStroke,
      continueStroke,
      endStroke,
      resetToOriginal,
      resetState,
    }
  },
}
</script>

<style scoped>
.image-processing {
  border: 3px solid #7367f0;
  animation: pulse-brightness 1.5s ease-in-out infinite;
}

@keyframes pulse-brightness {
  0%, 100% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.25);
  }
}
</style>
